// googleSearch.ts
// Google Custom Search integration with TypeScript

interface SearchResult {
  title: string;
  link: string;
  snippet: string;
  displayLink?: string;
}

interface GoogleSearchResponse {
  items?: Array<{
    title: string;
    link: string;
    snippet: string;
    displayLink?: string;
  }>;
  error?: {
    message: string;
    code: number;
  };
}

export class GoogleSearchModule {
  private apiKey: string;
  private cseId: string;
  private baseUrl = 'https://www.googleapis.com/customsearch/v1';

  constructor(apiKey: string, cseId: string) {
    this.apiKey = apiKey;
    this.cseId = cseId;
  }

  setCredentials(apiKey: string, cseId: string): void {
    this.apiKey = apiKey;
    this.cseId = cseId;
  }

  private optimizeQuery(query: string): string {
    // Remove common filler words and clean up the query
    const fillerWords = ['what', 'is', 'are', 'how', 'does', 'can', 'could', 'would', 'should', 'the', 'a', 'an'];
    let optimized = query.toLowerCase();
    
    // Remove question marks and excessive punctuation
    optimized = optimized.replace(/[?!]+/g, '');
    
    // Remove filler words at the beginning
    const words = optimized.split(' ');
    while (words.length > 0 && fillerWords.includes(words[0])) {
      words.shift();
    }
    
    // Add year for recent information if relevant
    const currentYear = new Date().getFullYear();
    const timeKeywords = ['latest', 'recent', 'current', 'new', 'updated', '2024', '2025'];
    if (timeKeywords.some(keyword => optimized.includes(keyword))) {
      if (!optimized.includes(currentYear.toString())) {
        words.push(currentYear.toString());
      }
    }
    
    // Focus on key terms
    const importantWords = words.filter(word => 
      word.length > 2 && !fillerWords.includes(word)
    );
    
    // Limit to most important terms (Google works better with fewer, focused terms)
    const finalQuery = importantWords.slice(0, 6).join(' ');
    
    return finalQuery || query; // Fallback to original if optimization fails
  }

  async search(query: string, numResults: number = 5): Promise<SearchResult[]> {
    if (!this.apiKey || !this.cseId) {
      throw new Error('Google Search API credentials not configured');
    }

    const optimizedQuery = this.optimizeQuery(query);
    console.log('Original query:', query);
    console.log('Optimized query:', optimizedQuery);

    const params = new URLSearchParams({
      key: this.apiKey,
      cx: this.cseId,
      q: optimizedQuery,
      num: numResults.toString()
    });

    try {
      const response = await fetch(`${this.baseUrl}?${params}`);
      const data: GoogleSearchResponse = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Search API request failed');
      }

      if (!data.items || data.items.length === 0) {
        return [];
      }

      return data.items.map(item => ({
        title: this.escapeMarkdownLeadingChars(item.title),
        link: item.link,
        snippet: this.escapeMarkdownLeadingChars(item.snippet),
        displayLink: item.displayLink
      }));
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  private escapeMarkdownLeadingChars(text: string): string {
    if (!text) return text;
    
    // List of Markdown special characters that need escaping when at the start of a line
    const leadingSpecialChars = /^([#*\->+\d\.`~])/gm;
    
    // Escape leading special characters with backslash
    return text.replace(leadingSpecialChars, '\\$1');
  }

  formatResults(results: SearchResult[]): string {
    if (results.length === 0) {
      return 'No search results found.';
    }

    const formatted = results.map((result, index) => {
      const domain = result.displayLink || new URL(result.link).hostname;
      return `${index + 1}. **${result.title}**\n   ${domain}\n   ${result.snippet}\n   [Read more](${result.link})`;
    }).join('\n\n');

    return `## Search Results\n\n${formatted}`;
  }
}